import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Section, Steps } from "../type";
import { routeUrls } from "../../../navigation/routeUrls";
import svgIcons from "../../../assets/svg/svgIcons";
import HelpFeaturesContent from "./helpFeaturesContent";
import AppSearchBox from "../../../components/searchBox";
import { useTranslation } from "react-i18next";
import { useFetchArticleBySlugApiQuery } from "../../../api/network/helpApiServices";


const HelpFeatures = () => {
  const { t } = useTranslation("translation", { keyPrefix: "helpSection" });
  const { slug } = useParams();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const [scrollToKeyword, setScrollToKeyword] = useState("");
  const location = useLocation();
  const [subSection, setSubSection] = useState("");

  const {
    data: sectionArticle,
    isFetching: sectionLoading,
  } = useFetchArticleBySlugApiQuery(slug!, { skip: !slug });

  // For Help main page search: receives the itemMatch to set the activeIndex in sectionArticle, 
  // cleans up the matched text to remove HTML tags and then scrolls to the content
  useEffect(() => {
    if (sectionLoading) {
      return;
    }
    const { itemMatch } = location.state ?? {};
    if (itemMatch) {
      setActiveIndex(sectionArticle?.steps.sections.findIndex(s => s.title === itemMatch.matched_section) ?? 0);
      const cleanText = itemMatch.search_preview.replace(/<\/?mark>/g, "").replace(/<\/?b>/g, "").replace(/\.\.\./g, "");
      setScrollToKeyword("");
      setTimeout(() => setScrollToKeyword(cleanText), 0);
    }
  }, [sectionLoading, sectionArticle, location.state]);


  // For search in the article page: first retrieves the matches checking the section title and description,
  // then retrieves the matches inside section content. This is combined as searchKeyResults.
  const searchKeyResults = useMemo((): { matchedSection: Section, subSectionMatches: string[] }[] => {
    if (!searchQuery || searchQuery.length < 3) return [];
    if (!sectionArticle) return [];
    const q = searchQuery.toLowerCase();
    const sectionMatches: { matchedSection: Section, subSectionMatches: string[] }[] = [];
    const subSectionMatches: { matchedSection: Section, subSectionMatches: string[] }[] = [];

    (sectionArticle.steps as Steps)?.sections?.forEach((section) => {
      const inSection =
        section.title.toLowerCase().includes(q) ||
        section.Description?.toLowerCase().includes(q);
      if (inSection) {
        sectionMatches.push({ matchedSection: section, subSectionMatches: [] });
      }

      const allSubSectionMatches = section.sub_section?.filter(ss =>
        ss.content?.some(c =>
          c.toLowerCase().includes(q)
        )
      ).map(ss => ss.title) ?? [];
      if (allSubSectionMatches.length > 0) {
        subSectionMatches.push({ matchedSection: section, subSectionMatches: allSubSectionMatches });
      }
    });

    return [...sectionMatches,
    ...subSectionMatches];
  }, [searchQuery, sectionArticle]);

  // Handling search in the article page: setting activeIndex and scroll keyword
  const handleSearchResultClick = (item: Section, subSectionMatch: string | undefined) => {
    setActiveIndex(sectionArticle?.steps.sections.findIndex(s => s.title === item.title) ?? 0);
    if (subSectionMatch) {
      setScrollToKeyword("");
      setTimeout(() => {
        setSubSection(subSectionMatch);
        setScrollToKeyword(searchQuery);
      }, 0)
    }
    setSearchQuery("");
  }

  // For scrolling to matched section
  useEffect(() => {
    if (!scrollToKeyword || !contentRef.current) return;
    const timeout = setTimeout(() => {
      const allElements = subSection !== ""
        ? contentRef.current!.querySelector(`[data-subsection="${subSection}"]`)?.querySelectorAll("li, span")
        : contentRef.current!.querySelectorAll("li, span");

      if (!allElements) return;
      const el = Array.from(allElements).find(el =>
        el.textContent?.toLowerCase().includes(scrollToKeyword.toLowerCase())
      );

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setScrollToKeyword("");
      setSubSection("");
    }, 500);
    return () => { clearTimeout(timeout); }
  }, [scrollToKeyword, subSection]);


  if (sectionArticle) {
    return (
      <div className="max-w-7xl px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <div className="flex items-start">
            <img
              src={svgIcons.LeftArrowIcon}
              alt="left-arrow-icon"
              className="mr-4 p-2 bg-accent-blue-pale rounded-full cursor-pointer"
              onClick={() => {
                navigate(routeUrls.dashboardChildren.help);
              }}
            />
          </div>
          <Link to={routeUrls.dashboardChildren.help} className="hover:underline">
            {t("heading")}
          </Link>
          <span>{">"}</span>
          <span className="text-gray-700 font-medium">{sectionArticle.title}</span>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row">
          <div className="flex flex-col gap-2 w-full lg:w-60">
            <h2 className="text-xl font-bold mb-2">{sectionArticle.title}</h2>

            <div className="relative" ref={searchBoxRef}>
              <AppSearchBox
                placeholder="Search help articles..."
                onChange={(e) => {
                  if (e.target.value.length === 0 || e.target.value.length >= 3) setSearchQuery(e.target.value);
                }}
                onClear={() => {
                  setSearchQuery("");
                }}
              />
              {searchQuery.length >= 3 && (
                <div className="flex flex-col justify-between absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-md shadow-lg p-2 gap-1 max-h-96 overflow-y-auto max-w-full">
                  {searchKeyResults.length === 0 ? (
                    <p className="w-full text-gray-500 mt-2 px-4 py-3 text-sm">{t("no_results_found")}</p>
                  ) : (
                    searchKeyResults.map(({ matchedSection, subSectionMatches }, index) => {
                      // If there are subsection matches, render one button per match
                      if (subSectionMatches.length > 0) {
                        return subSectionMatches.map((subSection, ssIndex) => (
                          <button
                            key={`${index}-${ssIndex}`}
                            className="flex-grow w-full flex items-start gap-3 px-4 py-3 hover:bg-accent-blue-paleO66 text-left hover:rounded-lg break-words"
                            onClick={() => handleSearchResultClick(matchedSection, subSection)}
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold">
                                <span className="text-field-label-valid">{matchedSection.title}</span>
                                <span className="mx-1.5">›</span>
                                <span className="text-sidemenu-submenu-text-active">{subSection}</span>
                              </p>
                            </div>
                          </button>
                        ));
                      }

                      // Otherwise render a single button for the section
                      return (
                        <button
                          key={index}
                          className="flex-grow w-full flex items-start gap-3 px-4 py-3 hover:bg-accent-blue-paleO66 text-left hover:rounded-lg break-words"
                          onClick={() => handleSearchResultClick(matchedSection, undefined)}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-semibold">
                              <span className="text-field-label-valid">{matchedSection.title}</span>
                            </p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            <div className="w-full lg:w-60 bg-white">
              <div className="grid auto-rows-fr">
                {sectionArticle.steps.sections.map((section, index) => {
                  const isActive = activeIndex === index;
                  return (
                    <button
                      key={index}
                      onClick={() => setActiveIndex(index)}
                      className={`w-full h-full flex items-center gap-1 justify-between px-4 py-3 text-sm text-left transition
                       ${isActive
                          ? "bg-accent-blue-paleO66 text-accent-blue-dark font-medium border-blue-600"
                          : "text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-semibold font-sm">{section.title}</span>
                      </div>

                      <span className="text-gray-400">{">"}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <HelpFeaturesContent ref={contentRef} activeIndex={activeIndex} sectionArticle={sectionArticle} />

        </div>
      </div>
    );
  }
  else {
    return (
      <div className="max-w-7xl px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <div className="flex items-start">
            <img
              src={svgIcons.LeftArrowIcon}
              alt="left-arrow-icon"
              className="mr-4 p-2 bg-accent-blue-pale rounded-full cursor-pointer"
              onClick={() => navigate(routeUrls.dashboardChildren.help)}
            />
          </div>
        </div>

        {sectionLoading ? (
          <div className={`relative z-10 w-full h-48`}>
            <img src={svgIcons.LoadingAnimation} alt="loading-animation" className="size-16 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
        ) : (
          <div className="p-4 text-gray-500 flex items-center justify-center">{t("no_results_found")}</div>
        )}
      </div>
    );
  }
};

export default HelpFeatures;